/**
 * Platform admin card for the pgvector store behind the RAG knowledge base
 * (ADM-R01, CLAUDE.md §3.6). Wires, through adminRAGAPI:
 *   GET  /api/documents/admin/vector-health            (pgvector version, chunk counts)
 *   POST /api/documents/admin/cleanup-orphaned-chunks  (drop chunks of deleted documents)
 * Chunk statistics live on the Documents screen (/api/documents/admin/stats).
 */
import React, { useState } from 'react';
import { View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminKeyValueList, AdminSectionCard, AdminStatusPill } from '@/components/admin/AdminUI';
import { adminRAGAPI, type VectorStoreHealth } from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import { confirmDialog } from '../../utils/dialogs';

export default function VectorStoreCard() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [health, setHealth] = useState<VectorStoreHealth | null>(null);
  const [checking, setChecking] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const handleCheck = async () => {
    setChecking(true);
    try {
      setHealth(await adminRAGAPI.getVectorHealth());
    } catch {
      setHealth(null);
      showToast(t('platformAdmin.vectorStore.checkError'), 'error');
    } finally {
      setChecking(false);
    }
  };

  const handleCleanup = async () => {
    const confirmed = await confirmDialog(
      t('platformAdmin.vectorStore.cleanupConfirmTitle'),
      t('platformAdmin.vectorStore.cleanupConfirmMessage'),
      t('platformAdmin.vectorStore.cleanupAction'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    setCleaning(true);
    try {
      await adminRAGAPI.cleanupOrphanedChunks();
      showToast(t('platformAdmin.vectorStore.cleanupSuccess'), 'success');
      if (health) await handleCheck();
    } catch {
      showToast(t('platformAdmin.vectorStore.cleanupError'), 'error');
    } finally {
      setCleaning(false);
    }
  };

  const healthy = health?.status === 'ok';

  return (
    <AdminSectionCard
      icon="storage"
      title={t('platformAdmin.vectorStore.title')}
      subtitle={t('platformAdmin.vectorStore.description')}
    >
      <View className="flex-row flex-wrap" style={{ gap: 8 }}>
        <Button variant="outline" size="sm" onPress={() => void handleCheck()} disabled={checking}>
          <Text className="text-xs font-semibold">
            {checking ? t('common.loading') : t('platformAdmin.vectorStore.checkAction')}
          </Text>
        </Button>
        <Button size="sm" onPress={() => void handleCleanup()} disabled={cleaning}>
          <Text className="text-xs font-semibold text-primary-foreground">
            {cleaning ? t('common.loading') : t('platformAdmin.vectorStore.cleanupAction')}
          </Text>
        </Button>
      </View>
      {health && (
        <View testID="vector-store-health" style={{ gap: 6 }}>
          <AdminStatusPill
            label={healthy ? t('platformAdmin.vectorStore.statusOk') : t('platformAdmin.vectorStore.statusError')}
            tone={healthy ? 'success' : 'error'}
          />
          <AdminKeyValueList
            rows={[
              { label: t('platformAdmin.vectorStore.pgvectorVersion'), value: health.pgvector_version || '-' },
              { label: t('platformAdmin.vectorStore.totalChunks'), value: String(health.total_chunks ?? '-') },
              { label: t('platformAdmin.vectorStore.embeddedChunks'), value: String(health.embedded_chunks ?? '-') },
            ]}
          />
        </View>
      )}
    </AdminSectionCard>
  );
}
